import { Navigate, Route, Routes, useLocation } from "react-router-dom";
import { Tablet } from "lucide-react";
import Board from "@/pages/Board";
import FirstThen from "@/pages/FirstThen";
import ChoiceBoard from "@/pages/ChoiceBoard";
import Login from "@/pages/Login";
import AuthCallback from "@/pages/AuthCallback";
import ProtectedRoute from "@/components/ProtectedRoute";
import {
  getChildDeviceMarker,
  usePairingStatusPoll,
  clearChildDeviceMarker,
} from "@/lib/childDevice";

/**
 * Friendly "paired but no profile yet" landing — used as the fallback when
 * the parent hasn't picked a default_profile_id. Avoids the
 * /profiles → /login → /profiles redirect loop the unbounded Navigate
 * fallback used to cause.
 */
function PairedWaitingScreen({ label }) {
  return (
    <div className="min-h-screen flex items-center justify-center px-4 bg-[color:var(--cream)]">
      <div className="brutal-card p-8 max-w-md text-center bg-[color:var(--paper)]">
        <div className="inline-flex items-center justify-center w-14 h-14 rounded-2xl border-[3px] border-[color:var(--ink)] bg-[color:var(--gold)] mb-3">
          <Tablet size={28} aria-hidden />
        </div>
        <h1 className="text-2xl font-extrabold mb-2">Tablet paired</h1>
        <p className="text-sm text-[color:var(--ink)]/70">
          This is <strong>{label || "a child tablet"}</strong>. A grown-up
          needs to pick which profile to show. Open SonSpeak on your phone or
          laptop and choose a default profile under <em>Admin → Child devices</em>.
        </p>
      </div>
    </div>
  );
}

/**
 * Locked router for a paired child tablet.
 *
 * Only communication pages are reachable. Any other URL silently redirects
 * to the default profile's board (or shows the waiting screen if no
 * default was set). Settings, admin, billing, pricing, blog, etc. simply
 * don't exist for this device.
 *
 * The marker is reconciled with the backend every 10s — when the parent
 * unpairs from their admin device, this component forces a full reload so
 * the app re-evaluates and renders the normal shell.
 */
export default function ChildDeviceShell() {
  const location = useLocation();
  const marker = getChildDeviceMarker();
  const defaultProfileId = marker?.default_profile_id;
  const label = marker?.label;

  // Hook must run unconditionally — placed before any early return so
  // React's hook order stays stable across renders. The hook itself
  // no-ops when the marker is cleared mid-poll.
  usePairingStatusPoll({
    enabled: true,
    onUnpaired: () => {
      // Reload so AppRouter re-evaluates isChildDevice() → false and the
      // normal shell takes over. Marker has already been cleared by the
      // poll helper.
      window.location.reload();
    },
  });

  // OAuth callback must still work even on a paired device — the parent
  // might re-auth after a token expiry while the tablet is in the room.
  const hash = location.hash || window.location.hash || "";
  if (hash.includes("session_id=")) return <AuthCallback />;

  // Fallback for unknown paths: jump straight to the child's board if we
  // know it, otherwise render an inline "waiting" card. We deliberately do
  // NOT bounce to /login here — the Login page redirects authenticated
  // users back to /profiles, which would loop us right back through this
  // wildcard.
  const fallback = defaultProfileId ? (
    <Navigate to={`/board/${defaultProfileId}`} replace />
  ) : (
    <PairedWaitingScreen label={label} />
  );

  return (
    <Routes>
      {/* Login is allowed so a token refresh can happen on the same tablet
          without forcing the parent to find another device. */}
      <Route path="/login" element={<Login />} />
      <Route
        path="/board/:profileId"
        element={
          <ProtectedRoute>
            <Board />
          </ProtectedRoute>
        }
      />
      <Route
        path="/first-then/:profileId"
        element={
          <ProtectedRoute>
            <FirstThen />
          </ProtectedRoute>
        }
      />
      <Route
        path="/choice/:profileId"
        element={
          <ProtectedRoute>
            <ChoiceBoard />
          </ProtectedRoute>
        }
      />
      {/* Anything else: silently render the fallback. The child cannot
          navigate to Settings, billing, admin, etc. */}
      <Route path="*" element={fallback} />
    </Routes>
  );
}

/** Escape hatch for testing/dev only — never expose to UI. */
export function _devClearChildDevice() {
  clearChildDeviceMarker();
  window.location.reload();
}
